/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import cx from 'classnames';
import { type CSSProperties } from 'react';
import styles from './styles.module.scss';

type GapUnit = 0 | 1 | 2 | 3 | 4 | 5 | 6;

type FlexGridGap = {
  sm?: GapUnit;
  md?: GapUnit;
  lg?: GapUnit;
  xl?: GapUnit;
};

export type FlexGridProps = {
  as?: 'div' | 'span';
  className?: string;
  gap?: GapUnit | FlexGridGap;
  gapOverride?: CSSProperties['gap'];
  flexDirection?: CSSProperties['flexDirection'];
  alignItems?: CSSProperties['alignItems'];
  justifyContent?: CSSProperties['justifyContent'];
  padding?: CSSProperties['padding'];
  children: React.ReactNode;
  'data-testid'?: string;
};

export function FlexGrid({
  as: Component = 'div',
  className,
  gap,
  gapOverride,
  flexDirection,
  alignItems,
  justifyContent,
  padding,
  children,
  'data-testid': dataTestId,
}: FlexGridProps) {
  const defaultGap = typeof gap === 'object' ? undefined : gap;
  const { sm, md, lg, xl } = typeof gap === 'object' ? gap : ({} as FlexGridGap);

  return (
    <Component
      className={cx(
        styles.flexGridContainer,
        {
          [styles[`gap-default-${defaultGap}`]]: typeof defaultGap === 'number',
          [styles[`gap-sm-${sm}`]]: typeof sm === 'number',
          [styles[`gap-md-${md}`]]: typeof md === 'number',
          [styles[`gap-lg-${lg}`]]: typeof lg === 'number',
          [styles[`gap-xl-${xl}`]]: typeof xl === 'number',
        },
        className
      )}
      style={{
        gap: gapOverride,
        flexDirection,
        alignItems,
        justifyContent,
        padding,
      }}
      data-testid={dataTestId}
    >
      {children}
    </Component>
  );
}
